const GREY_MAP_STYLE = [
  { stylers: [{ saturation: -100 }, { lightness: 10 }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9aa0a6" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "water", stylers: [{ lightness: 25 }] },
  { featureType: "road", elementType: "geometry", stylers: [{ lightness: 20 }] },
];

export const buildStyles = (w, h, count = 1, scale = 1) => {
  const u = (Math.min(w, h) / 100) * scale;
  const v = (n) => `${n * u}px`;

  const n = Math.max(1, Number(count) || 1);
  const iw = (w / n) * scale;
  const ih = (h / n) * scale;

  const routeWeight = Math.max(3, u * 0.85);

  const is98 = Math.abs(w / h - 9 / 8) < 0.06; 
  const is89 = Math.abs(w / h - 8 / 9) < 0.05; 
  const is49 = Math.abs(w / h - 4 / 9) < 0.04; 
  const is39 = Math.abs(w / h - 3 / 9) < 0.03; 
  const is210 = Math.abs(w / h - 2 / 10) < 0.02; 
  const isFooter = w / h > 2.5; 
  const is93 = Math.abs(w / h - 9 / 3) < 0.2; 
  const carScale = isFooter ? 1.56 : 1; 
  const ftLabelScale = is93 ? 0.7 : 1;

  // Deeper, three-stop green: the lighter mid stop gives the panel a sheen
  // instead of a flat wash, and the darker ends let white type and the
  // translucent cards read as raised. Plain linear-gradient — unprefixed
  // since Chrome 26, so it is safe on the Chrome 60 signage players.
  const GREEN =
    "linear-gradient(145deg, #0a4430 0%, #0e6b3e 55%, #093c2b 100%)";

  // ---- data-only (map hidden) sizing -------------------------------------
  // Without the map the panel owns the whole screen, so `u` (min-dimension
  // based) leaves tall/skinny displays badly under-filled. `nu` boosts the
  // unit by how far the screen is from square on its long axis; wide screens
  // are already height-constrained so they stay at 1x.
  const ratio = w / h;
  const nmBoost = ratio >= 1 ? 1 : Math.min(2.3, Math.sqrt(1 / ratio));
  const nu = (Math.min(w, h) / 100) * nmBoost;
  const nv = (x) => `${x * nu}px`;
  // Is there room to sit the pickup beside the badge, or must it take its own
  // header row? nmCards has to know too, so the list height budget is right.
  const fromInline = w >= nu * 70;
  // Two destinations read better as a left/right pair than as two very wide,
  // short bands — but only where the screen is wider than it is tall, or each
  // column would be a sliver.
  const sideBySidePair = w / h >= 1.2;

  const styles = {
    is98,
    is89,
    is49,
    is39,
    is210,
    wrapper: {
      width: w + "px",
      height: h + "px",
      display: "flex",
      overflow: "hidden",
      fontWeight: 800,
      fontFamily:
        "'Segoe UI', system-ui, -apple-system, Roboto, Arial, sans-serif",
    },
    mapSide: { flex: 1, minWidth: 0, minHeight: 0 },
    mapContainer: { width: "100%", height: "100%" },
    mapOptions: {
      disableDefaultUI: true,
      zoomControl: false,
      gestureHandling: "none",
      keyboardShortcuts: false,
      styles: GREY_MAP_STYLE,
    },
    routePolyline: { strokeWeight: routeWeight },
    carBadge: {
      display: "inline-flex",
      alignItems: "center",
      background: "#ffffff",
      color: "#3c4043",
      border: `${v(0.15)} solid rgba(0,0,0,0.15)`,
      borderRadius: v(0.8),
      padding: `${v(0.8)} ${v(1.4)}`,
      whiteSpace: "nowrap",
      boxShadow: `0 ${v(0.15)} ${v(0.55)} rgba(0,0,0,0.25)`,
    },
    carBadgeFast: {
      display: "inline-flex",
      alignItems: "center",
      background: "#ffffff",
      color: "#3c4043",
      border: `${v(0.15)} solid #6C4DF6`,
      borderRadius: v(0.8),
      padding: `${v(0.8)} ${v(1.4)}`,
      whiteSpace: "nowrap",
      boxShadow: `0 ${v(0.3)} ${v(0.85)} rgba(0,0,0,0.3)`,
    },
    carIcon: {
      width: v(1.7 * carScale),
      height: v(1.7 * carScale),
      flexShrink: 0,
      display: "block",
      marginRight: v(0.9),
    },
    carBadgeText: { display: "flex", flexDirection: "column", lineHeight: 1.1 },
    carBadgeTime: { fontSize: v(1.82 * carScale), fontWeight: 800 },
    carBadgeDist: { fontSize: v(1.4 * carScale), fontWeight: 800, opacity: 0.7 },

    panel: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      boxSizing: "border-box",
      background: GREEN,
      color: "#ffffff",
      display: "flex",
      flexDirection: "column",
      padding: is98 ? `${v(3.5)} ${v(1.875)}` : v(3.5),
      overflow: "hidden",
    },
    header: {
      display: "flex",
      justifyContent: "flex-end",
      width: "100%",
      flexShrink: 0,
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      background: "rgba(255,255,255,0.92)",
      color: "#127c3c",
      borderRadius: v(2),
      padding: `${v(1)} ${v(2.2)}`,
      fontSize: v(2.6),
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    dotLive: {
      width: v(1.6),
      height: v(1.6),
      borderRadius: "50%",
      background: "#17a34a",
      display: "inline-block",
      flexShrink: 0,
      marginRight: v(1),
    },

    content: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
      width: "100%",
      textAlign: "center",
    },
    from: { fontSize: v(3.4), fontWeight: 800, opacity: 0.95 },
    to: { fontSize: v(5.2), fontWeight: 800, lineHeight: 1.15, marginTop: v(2.5) },
    distance: {
      fontSize: v(15),
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.02em",
      marginTop: v(3),
    },
    mins: { fontSize: v(7), fontWeight: 800, lineHeight: 1, marginTop: v(1.5) },
    trafficPill: {
      marginTop: v(3),
      background: "rgba(255,255,255,0.2)",
      color: "#ffffff",
      borderRadius: v(0.7),
      padding: `${v(1.2)} ${v(3)}`,
      fontSize: v(3.4),
      fontWeight: 800,
    },

    list: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "100%",
      overflow: "hidden",
    },
    row: {
      display: "flex",
      alignItems: "center",
      marginBottom: v(1.6),
      boxSizing: "border-box",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.075) 100%)",
      border: `${v(0.12)} solid rgba(255,255,255,0.2)`,
      borderRadius: v(2.4),
      padding: `${v(1.8)} ${v(2.4)}`,
      textAlign: "left",
      minWidth: 0,
      overflow: "hidden",
      boxShadow: `0 ${v(0.4)} ${v(1.3)} rgba(3,32,20,0.22)`,
    },
    rowDot: {
      width: ih * 0.05 + "px",
      height: ih * 0.05 + "px",
      borderRadius: "50%",
      flexShrink: 0,
      marginRight: v(1.8),
    },
    rowText: { flex: 1, minWidth: 0 },
    rowLabel: {
      fontSize: ih * 0.062 + "px",
      fontWeight: 800,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    rowMeta: {
      display: "flex",
      alignItems: "baseline",
      marginTop: ih * 0.016 + "px",
    },
    rowDist: { fontSize: ih * 0.088 + "px", fontWeight: 800, marginRight: v(1.4) },
    rowTime: { fontSize: ih * 0.058 + "px", opacity: 0.95 },
    rowTrafficWrap: { marginTop: ih * 0.02 + "px" },
    rowRoute: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: v(1.4),
      background: "rgba(255,255,255,0.16)",
      borderRadius: v(1.2),
      padding: `${v(0.5)} ${v(1.6)}`,
      fontSize: ih * 0.05 + "px",
      fontWeight: 800,
      lineHeight: 1,
      whiteSpace: "nowrap",
    },
    // white chip with the traffic colour as text — a coloured fill would
    // vanish now that the row itself carries the traffic colour
    rowTraffic: {
      display: "inline-block",
      fontSize: ih * 0.046 + "px",
      fontWeight: 800,
      background: "#ffffff",
      borderRadius: v(1.2),
      padding: `${v(0.5)} ${v(1.6)}`,
    },

    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "1fr 1fr",
      gridGap: v(1.6),
      width: "100%",
      flex: 1,
      minHeight: 0,
      overflow: "hidden",
    },

    squarePanel: {
      flex: 0.45,
      minHeight: 0,
      boxSizing: "border-box",
      background: GREEN,
      color: "#ffffff",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-evenly",
      alignItems: "center",
      padding: `${v(2.31)} ${v(3.24)}`,
      overflow: "hidden",
      fontFamily:
        "'Segoe UI', system-ui, -apple-system, Roboto, Arial, sans-serif",
    },
    squareMapSide: {
      flex: 0.55,
      minWidth: 0,
      minHeight: 0,
      boxSizing: "border-box",
      background: GREEN,
      padding: `0 ${v(3.5)} ${v(3.5)}`,
    },
    squareHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      width: "100%",
      flexShrink: 0,
    },
    squareTrafficPill: {
      display: "inline-flex",
      alignItems: "center",
      background: "rgba(255,255,255,0.92)",
      borderRadius: v(2),
      padding: `${v(1)} ${v(2.2)}`,
      fontSize: v(2.6),
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    squareFrom: { fontSize: v(3.2), fontWeight: 800, opacity: 0.95 },
    squareTo: {
      width: "100%",
      textAlign: "center",
      fontSize: v(3.85),
      fontWeight: 800,
      lineHeight: 1.1,
    },
    squareKmRow: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "center",
    },
    squareKm: {
      fontSize: v(9),
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.02em",
      marginRight: v(2),
    },
    squareMins: { fontSize: v(5), fontWeight: 800, lineHeight: 1 },

    footerPanel: {
      flex: 0.62,
      minWidth: 0,
      boxSizing: "border-box",
      background: GREEN,
      color: "#ffffff",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "space-evenly",
      padding: `${v(2.5)} ${v(8.5)}`,
      overflow: "hidden",
      fontFamily:
        "'Segoe UI', system-ui, -apple-system, Roboto, Arial, sans-serif",
    },
    footerMapSide: { flex: 0.38, minWidth: 0, minHeight: 0 },
    footerItems: {
      flex: 1,
      width: "100%",
      minHeight: 0,
      display: "flex",
      alignItems: "stretch",
      overflow: "hidden",
      marginLeft: "-" + v(0.8),
      marginRight: "-" + v(0.8),
    },

    footerFromTo: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      minWidth: 0,
      marginTop: v(1.2),
      marginBottom: v(1.2),
    },
    footerFromLabel: {
      fontSize: ih * 0.075 * ftLabelScale + "px",
      fontWeight: 800,
      opacity: 0.9,
    },
    footerFromToVal: {
      fontSize: ih * 0.085 * ftLabelScale + "px",
      fontWeight: 800,
    },
    footerHero: { display: "flex", alignItems: "baseline" },
    footerHeroKm: {
      fontSize: ih * 0.182 * (is93 ? 0.8 : 1) + "px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.02em",
      marginRight: ih * 0.0455 + "px",
    },
    footerHeroMins: { fontSize: ih * 0.12 + "px", fontWeight: 800, lineHeight: 1 },
    footerTrafficPill: {
      alignSelf: "flex-start",
      background: "rgba(255,255,255,0.2)",
      color: "#ffffff",
      borderRadius: v(0.7),
      padding: `${ih * 0.02}px ${ih * 0.045}px`,
      fontSize: ih * 0.06 + "px",
      fontWeight: 800,
    },
    footerItem: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      boxSizing: "border-box",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.075) 100%)",
      border: `${v(0.12)} solid rgba(255,255,255,0.2)`,
      borderRadius: v(2),
      padding: v(2.2),
      overflow: "hidden",
      marginLeft: v(0.8),
      marginRight: v(0.8),
      boxShadow: `0 ${v(0.4)} ${v(1.3)} rgba(3,32,20,0.22)`,
    },
    footerHead: {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
      marginBottom: v(1.4),
    },
    footerLabel: {
      fontSize: iw * 0.05 + "px",
      fontWeight: 800,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    footerMeta: {
      display: "flex",
      alignItems: "center",
      fontSize: iw * 0.05 + "px",
      marginBottom: v(1.4),
    },
    footerDist: { fontSize: iw * 0.11 + "px", fontWeight: 800, lineHeight: 1, marginRight: v(1.4) },

    // ---- data-only (map hidden) styles ------------------------------------
    nmRoot: {
      width: w + "px",
      height: h + "px",
      boxSizing: "border-box",
      background: GREEN,
      color: "#ffffff",
      display: "flex",
      flexDirection: "column",
      padding: isFooter ? `${nv(3)} ${nv(4.5)}` : `${nv(4.5)} ${nv(5)}`,
      overflow: "hidden",
      position: "relative",
      fontWeight: 800,
      fontFamily:
        "'Segoe UI', system-ui, -apple-system, Roboto, Arial, sans-serif",
    },
    // Two soft radial lights — one warm highlight top-right, one dark pool
    // bottom-left — so the panel has a direction of light rather than reading
    // as a flat fill. radial-gradient is unprefixed from Chrome 26.
    nmGlow: {
      position: "absolute",
      top: "-" + nv(20),
      right: "-" + nv(14),
      width: nv(60),
      height: nv(60),
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(150,245,190,0.13) 0%, rgba(150,245,190,0) 72%)",
      pointerEvents: "none",
      zIndex: 0,
    },
    nmGlowLow: {
      position: "absolute",
      bottom: "-" + nv(24),
      left: "-" + nv(16),
      width: nv(64),
      height: nv(64),
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(3,42,27,0.35) 0%, rgba(3,42,27,0) 70%)",
      pointerEvents: "none",
      zIndex: 0,
    },
    nmTopBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      flexShrink: 0,
      position: "relative",
      zIndex: 1,
    },
    nmBadge: {
      display: "inline-flex",
      alignItems: "center",
      background: "#ffffff",
      color: "#0b6b39",
      borderRadius: nv(2),
      padding: `${nv(0.9)} ${nv(2)}`,
      fontSize: nv(3.1),
      fontWeight: 800,
      whiteSpace: "nowrap",
      boxShadow: `0 ${nv(0.35)} ${nv(1.1)} rgba(3,32,20,0.28)`,
    },
    nmDot: {
      width: nv(1.4),
      height: nv(1.4),
      borderRadius: "50%",
      background: "#17a34a",
      display: "inline-block",
      flexShrink: 0,
      marginRight: nv(1),
      boxShadow: `0 0 0 ${nv(0.45)} rgba(23,163,74,0.25)`,
    },
    // The pickup is shared by every destination card, so it reads once in the
    // header. It sits beside the badge when there is width for it, and drops to
    // its own row on narrow displays where it would otherwise be crushed.
    nmFromInline: fromInline,
    nmSideBySidePair: sideBySidePair,
    nmFromRow: {
      display: "flex",
      alignItems: "baseline",
      width: "100%",
      minWidth: 0,
      flexShrink: 0,
      marginTop: nv(1.6),
      position: "relative",
      zIndex: 1,
    },
    nmFrom: {
      display: "flex",
      alignItems: "baseline",
      minWidth: 0,
      flexShrink: 1,
      overflow: "hidden",
    },
    nmFromLabel: {
      fontSize: nv(1.9),
      fontWeight: 800,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      opacity: 0.7,
      whiteSpace: "nowrap",
      marginRight: nv(1.2),
    },
    nmFromValue: {
      fontSize: nv(2.8),
      fontWeight: 800,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
    },
    nmBody: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-evenly",
      width: "100%",
      overflow: "hidden",
      position: "relative",
      zIndex: 1,
    },

    // journey rail: From ● ── ● To
    nmJourney: { display: "flex", width: "100%", minWidth: 0 },
    nmRail: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flexShrink: 0,
      marginRight: nv(1.8),
      paddingTop: nv(1.1),
      paddingBottom: nv(1.1),
    },
    nmRailDot: {
      width: nv(1.2),
      height: nv(1.2),
      borderRadius: "50%",
      background: "rgba(255,255,255,0.8)",
      flexShrink: 0,
    },
    nmRailLine: {
      width: nv(0.26),
      flex: 1,
      minHeight: nv(2),
      background: "rgba(255,255,255,0.4)",
      marginTop: nv(0.6),
      marginBottom: nv(0.6),
    },
    nmRailPin: {
      width: nv(1.7),
      height: nv(1.7),
      borderRadius: "50%",
      boxSizing: "border-box",
      background: "#ffffff",
      border: `${nv(0.42)} solid rgba(255,255,255,0.4)`,
      flexShrink: 0,
    },
    nmStops: { flex: 1, minWidth: 0 },
    nmStopLabel: {
      fontSize: nv(2.2),
      fontWeight: 800,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      opacity: 0.72,
      lineHeight: 1,
    },
    nmStopValue: {
      fontSize: nv(4.6),
      fontWeight: 800,
      lineHeight: 1.15,
      marginTop: nv(0.5),
      overflow: "hidden",
    },
    nmStopGap: { height: nv(2.2) },

    nmHeroLabel: {
      fontSize: nv(2.4),
      fontWeight: 800,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      opacity: 0.75,
      lineHeight: 1,
      marginBottom: nv(1.2),
    },
    nmHeroRow: { display: "flex", alignItems: "baseline", minWidth: 0 },
    nmHeroValue: {
      fontSize: nv(15),
      fontWeight: 800,
      lineHeight: 0.95,
      letterSpacing: "-0.03em",
    },

    nmStats: { display: "flex", width: "100%", minWidth: 0 },
    // Glass tile: top-lit gradient fill + hairline edge + cast shadow. No
    // backdrop-filter (Chrome 76+) — the layered fill does the same job on 60.
    nmTile: {
      flex: 1,
      minWidth: 0,
      boxSizing: "border-box",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.19) 0%, rgba(255,255,255,0.08) 100%)",
      border: `${nv(0.12)} solid rgba(255,255,255,0.2)`,
      borderRadius: nv(1.8),
      padding: `${nv(1.5)} ${nv(1.9)}`,
      marginRight: nv(1.4),
      overflow: "hidden",
      boxShadow: `0 ${nv(0.4)} ${nv(1.3)} rgba(3,32,20,0.22)`,
    },
    nmTileLabel: {
      fontSize: nv(2.1),
      fontWeight: 800,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      opacity: 0.75,
      lineHeight: 1,
      marginBottom: nv(0.9),
    },
    nmTileValue: {
      fontSize: nv(5.6),
      fontWeight: 800,
      lineHeight: 1.05,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // white chip + traffic-coloured text reads far better on green than a
    // coloured fill would
    nmPill: {
      display: "inline-flex",
      alignItems: "center",
      background: "#ffffff",
      borderRadius: nv(1.2),
      padding: `${nv(1)} ${nv(2)}`,
      fontSize: nv(3),
      fontWeight: 800,
      whiteSpace: "nowrap",
      boxShadow: `0 ${nv(0.3)} ${nv(1)} rgba(3,32,20,0.26)`,
    },
    nmPillDot: {
      width: nv(1.1),
      height: nv(1.1),
      borderRadius: "50%",
      background: "currentColor",
      display: "inline-block",
      flexShrink: 0,
      marginRight: nv(0.8),
    },

    // multi-destination cards
    nmList: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      justifyContent: "center",
      width: "100%",
      overflow: "hidden",
      position: "relative",
      zIndex: 1,
    },
    // Side-by-side cards stretch so a wrapped label on one doesn't leave the
    // row ragged. Safe here because a row list's cards are never far taller
    // than their content — the tall-card case only arises when they stack.
    nmListRow: { alignItems: "stretch" },
    nmError: {
      fontSize: nv(4.4),
      fontWeight: 800,
      color: "#fee2e2",
      textAlign: "center",
    },

    error: { fontSize: v(4), fontWeight: 800, color: "#fee2e2" },
    center: {
      width: w + "px",
      height: h + "px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background: "#0f172a",
      color: "#f8fafc",
      fontFamily: "Arial, sans-serif",
    },
  };

  // Some browser/font combos (notably Chromium 60 on Linux, where the font
  // stack resolves to a face with no real heavy weight) render font-weight:800
  // as thin. -webkit-text-stroke thickens the glyph outlines directly — it
  // does not depend on a bold font face — and using `em` keeps the stroke
  // proportional to each field's own font-size. Apply it to every text style.
  var boldStroke = "0.025em currentColor";
  var strokeAll = function (obj) {
    Object.keys(obj).forEach(function (k) {
      var st = obj[k];
      if (st && typeof st === "object" && st.fontSize) {
        st.WebkitTextStroke = boldStroke;
        // keep the stroke from eating into glyph counters too aggressively
        st.paintOrder = "stroke fill";
      }
    });
    return obj;
  };
  strokeAll(styles);

  // Content area of the side panel in each map layout, so nmCards can size
  // cards against the panel rather than the whole screen. The flex ratios
  // mirror the panel/mapSide values above; the header is the badge row.
  var panelPadX = is98 ? u * 1.875 : u * 3.5;
  var panelPadY = u * 3.5;
  var panelHead = u * 6;
  styles.mapPanelBox = {
    landscape: {
      w: w / 2 - panelPadX * 2,
      h: h - panelPadY * 2 - panelHead,
    },
    portrait: {
      w: w - panelPadX * 2,
      h: h / 2 - panelPadY * 2 - panelHead,
    },
    // square stacks map (flex .85) above panel (flex 1)
    square: {
      w: w - panelPadX * 2,
      h: (h * 1) / 1.85 - panelPadY * 2 - panelHead,
    },
    // footer panel is flex .62 of the width, with its own wider padding
    footer: {
      w: w * 0.62 - u * 8.5 * 2,
      h: h - u * 2.5 * 2 - panelHead,
    },
  };

  // Map-mode rows take the same solid traffic fill, so both modes read as one
  // product. Merged over `row` / `footerItem` by the layouts.
  styles.trafficFill = function (color) {
    if (!color) return {};
    return {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.17) 0%, rgba(255,255,255,0) 62%), " +
        color,
      border: `${v(0.12)} solid rgba(255,255,255,0.3)`,
      boxShadow: `0 ${v(0.5)} ${v(1.6)} rgba(3,32,20,0.36)`,
    };
  };

  // Destination cards, sized from the space a card actually gets rather than
  // from the screen's min dimension. On a 1920x1080 two-card list the old
  // min-dimension unit produced ~25px labels in a 440px-tall card — unreadable
  // on signage. Callers pass their grid shape: (1, n) stacked, (n, 1) across,
  // (2, ceil(n/2)) wrapped.
  // `box` overrides the container the cards are laid out in — map mode uses it
  // to pass the side panel's content area instead of the whole screen.
  styles.nmCards = function (cols, rows, box) {
    var c = Math.max(1, Number(cols) || 1);
    var r = Math.max(1, Number(rows) || 1);
    var padX = isFooter ? nu * 4.5 : nu * 5;
    var padY = isFooter ? nu * 3 : nu * 4.5;
    var gut = nu * 0.7;
    // the badge row eats roughly 6.2 units off the top, plus another 5.2 when
    // the pickup could not fit beside it and took its own row
    var headH = nu * (fromInline ? 6.2 : 11.4);
    var listW = box ? Math.max(60, box.w) : Math.max(60, w - padX * 2);
    var listH = box
      ? Math.max(60, box.h)
      : Math.max(60, h - padY * 2 - headH);
    var cardW = Math.max(40, listW / c - gut * 2);
    var cardH = Math.max(40, listH / r - gut * 2);
    var cu = cardH / 100;

    // Height drives the size; the width divisor is the clamp that stops a long
    // address or a narrow footer column from overflowing.
    var fit = function (byHeight, widthDiv) {
      return Math.max(9, Math.min(cardH * byHeight, cardW / widthDiv)) + "px";
    };
    var chip = fit(0.1, 23);
    // A long destination name wraps to two lines, so the label's share of the
    // card has to be budgeted at two lines or the time row below it gets
    // clipped by the card's overflow.
    var labelSize = parseFloat(fit(0.135, 17));
    var labelLines = 2;
    // On tall cards the width clamp binds long before the height one, so a
    // stretched card would leave the content floating in a mostly empty box.
    // Cards that stack vertically size to their content and the list centres
    // them; cards that sit side by side still share the width equally.
    var across = c > 1;
    // On a card that is not much wider than it is tall, width is what limits
    // the type. Dropping the distance onto its own line frees the duration to
    // use the full card width — roughly a 70% bigger numeral on portrait.
    var stack = cardW / cardH < 1.6;

    return strokeAll({
      card: {
        flex: across ? "1 1 0%" : "0 0 auto",
        minWidth: 0,
        minHeight: 0,
        maxHeight: cardH + "px",
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.075) 100%)",
        border: `${Math.max(1, cu * 0.35)}px solid rgba(255,255,255,0.2)`,
        borderRadius: cu * 6 + "px",
        // extra left padding clears the accent rail
        padding: `${cu * 7}px ${cu * 7}px ${cu * 7}px ${cu * 9}px`,
        margin: gut + "px",
        overflow: "hidden",
        boxShadow: `0 ${cu * 1.6}px ${cu * 5}px rgba(3,32,20,0.24)`,
      },
      // Absolutely positioned so the card keeps its full corner radius — a
      // thick coloured border-left would mitre against the hairline edge.
      accent: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: Math.max(3, cu * 2.6) + "px",
        borderTopLeftRadius: cu * 6 + "px",
        borderBottomLeftRadius: cu * 6 + "px",
      },
      label: {
        fontSize: labelSize + "px",
        fontWeight: 800,
        lineHeight: 1.15,
        maxHeight: labelSize * 1.15 * labelLines + "px",
        overflow: "hidden",
        flexShrink: 0,
      },
      row: {
        display: "flex",
        flexDirection: stack ? "column" : "row",
        alignItems: stack ? "flex-start" : "baseline",
        marginTop: cu * 3.2 + "px",
        flexShrink: 0,
      },
      time: {
        fontSize: stack ? fit(0.3, 4.8) : fit(0.3, 7.2),
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: "-0.02em",
        marginRight: stack ? 0 : cu * 5 + "px",
        marginBottom: stack ? cu * 2 + "px" : 0,
      },
      dist: {
        fontSize: stack ? fit(0.14, 9) : fit(0.15, 15),
        fontWeight: 800,
        opacity: 0.85,
      },
      foot: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        marginTop: cu * 3.2 + "px",
        flexShrink: 0,
      },
      pill: {
        display: "inline-flex",
        alignItems: "center",
        background: "#ffffff",
        borderRadius: cu * 3.4 + "px",
        padding: `${cu * 2.2}px ${cu * 4}px`,
        fontSize: chip,
        fontWeight: 800,
        whiteSpace: "nowrap",
        marginRight: cu * 4 + "px",
        boxShadow: `0 ${cu * 1}px ${cu * 3}px rgba(3,32,20,0.24)`,
      },
      pillDot: {
        width: parseFloat(chip) * 0.42 + "px",
        height: parseFloat(chip) * 0.42 + "px",
        borderRadius: "50%",
        background: "currentColor",
        display: "inline-block",
        flexShrink: 0,
        marginRight: cu * 2.2 + "px",
      },

      // Solid traffic colour. The gradient on top keeps the top-lit sheen so a
      // filled card still belongs to the same surface family as the glass one;
      // the brighter hairline and deeper shadow are what separate a Light
      // (green) card from the green panel behind it.
      tint: function (color) {
        if (!color) return {};
        return {
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.17) 0%, rgba(255,255,255,0) 62%), " +
            color,
          border: `${Math.max(1, cu * 0.35)}px solid rgba(255,255,255,0.3)`,
          boxShadow: `0 ${cu * 2}px ${cu * 6}px rgba(3,32,20,0.38)`,
        };
      },
      // Route colours are decorative without a map to match them to, and they
      // clash on a filled card — the rail becomes a plain highlight instead.
      // In map mode the A→B chip carries the identity link to the marker, so
      // nothing is lost there either.
      railTint: "rgba(255,255,255,0.55)",
      routeChip: {
        display: "inline-flex",
        alignItems: "center",
        background: "rgba(255,255,255,0.24)",
        borderRadius: cu * 3.4 + "px",
        padding: `${cu * 2.2}px ${cu * 4}px`,
        fontSize: chip,
        fontWeight: 800,
        lineHeight: 1,
        whiteSpace: "nowrap",
        marginRight: cu * 4 + "px",
      },
    });
  };

  return styles;
};

export default buildStyles;
